import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, TextField, Paper } from '@mui/material';
import { BsMicFill, BsMicMuteFill, BsClipboard, BsTrash } from 'react-icons/bs';
import useVoiceToText from '../hooks/useVoiceToText';

export default function AudioToText() {
  const { isListening, transcript, startListening, stopListening, isSupported, resetTranscript } = useVoiceToText({ continuous: true, interimResults: true });
  const [text, setText] = useState("");
  const [initialText, setInitialText] = useState("");

  const handleMicToggle = () => {
    if (isListening) {
      stopListening();
    } else {
      setInitialText(text);
      resetTranscript();
      startListening();
    }
  };

  useEffect(() => {
    if (isListening) {
       setText(initialText ? `${initialText} ${transcript}` : transcript);
    }
  }, [transcript, isListening]);

  const handleClear = () => {
    setText("");
    setInitialText("");
    resetTranscript();
    if (isListening) {
        stopListening();
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  if (!isSupported) {
    return (
      <Container maxWidth="md" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <Typography variant="h5" color="error">
          Speech Recognition is not supported in this browser. Please use Chrome, Edge, or Safari.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 1 }}>
          Audio Dictation
        </Typography>
        <Typography variant="body1" sx={{ color: '#64748b' }}>
          Click the microphone and start speaking to convert your voice to text.
        </Typography>
      </Box>

      <Paper elevation={0} sx={{ 
          p: 3, 
          borderRadius: '16px', 
          border: '1px solid #e2e8f0',
          background: '#ffffff',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
      }}>
        
        <TextField
          multiline
          fullWidth
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Your transcribed text will appear here..."
          variant="standard"
          InputProps={{
            disableUnderline: true,
            style: { 
                fontSize: '18px', 
                lineHeight: 1.6,
                color: '#334155'
            }
          }}
          sx={{
            flexGrow: 1,
            '& .MuiInputBase-root': {
                height: '100%',
                alignItems: 'flex-start'
            }
          }}
        />

        {/* Action Buttons Container */}
        <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mt: 3,
            pt: 2,
            borderTop: '1px dashed #e2e8f0'
        }}>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="outlined" 
                color="inherit" 
                startIcon={<BsClipboard />}
                onClick={handleCopy}
                disabled={!text}
                sx={{ borderRadius: '8px', color: '#475569', borderColor: '#cbd5e1' }}
              >
                Copy
              </Button>
              <Button 
                variant="outlined" 
                color="error" 
                startIcon={<BsTrash />}
                onClick={handleClear}
                disabled={!text && !isListening}
                sx={{ borderRadius: '8px' }}
              >
                Clear
              </Button>
          </Box>

          <Button
            variant="contained"
            onClick={handleMicToggle}
            sx={{
              borderRadius: '50px',
              py: 1.5,
              px: 4,
              fontSize: '16px',
              fontWeight: 'bold',
              textTransform: 'none',
              backgroundColor: isListening ? '#ef4444' : '#3b82f6',
              boxShadow: isListening ? '0 4px 14px 0 rgba(239, 68, 68, 0.39)' : '0 4px 14px 0 rgba(59, 130, 246, 0.39)',
              '&:hover': {
                backgroundColor: isListening ? '#dc2626' : '#2563eb',
              }
            }}
            startIcon={isListening ? <BsMicFill size={20} /> : <BsMicMuteFill size={20} />}
          >
            {isListening ? "Listening... Click to Stop" : "Start Recording"}
          </Button>

        </Box>
      </Paper>
    </Container>
  );
}
